/**
 * Home commercial hero — Tonight's Watch.
 * Continue on the loop · Needs You on the exception.
 * Frozen desks (Operations / Response / Resources / Machine / Activate) stay untouched.
 */
import { escapeHtml } from "./dom.js";
import { BRAND_TAGLINE } from "./brand.js";
import { operatorSay } from "../cmc/operator-say.js";
import { decisionSurfaceActionsHtml } from "./decision-surface.js";
import { maicFieldsFromAiItems } from "./response-loop-workbench.js";

const LAMP_ORDER = ["judge", "veto", "act", "prove", "stop"];

function _lampState(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "on" || s === "engaged") return s === "engaged" ? "engaged" : "on";
  if (s === "degraded") return "degraded";
  return "off";
}

function defaultLamps() {
  return [
    { id: "judge", state: "off", line: "No AI owner yet" },
    { id: "veto", state: "on", line: "Rules shadow; policy can veto" },
    { id: "act", state: "off", line: "Action is blocked" },
    { id: "prove", state: "off", line: "No proof yet" },
    { id: "stop", state: "on", line: "E-Stop remains available" },
  ];
}

function lampsOf(aiPack) {
  const pack = aiPack && typeof aiPack === "object" ? aiPack : {};
  const tonight = pack.tonight && typeof pack.tonight === "object" ? pack.tonight : {};
  const src = (tonight.lamps && tonight.lamps.lamps)
    || (pack.lamps && pack.lamps.lamps)
    || pack.lamps;
  const rows = Array.isArray(src) ? src.filter((x) => x && typeof x === "object") : [];
  if (!rows.length) return defaultLamps();
  const byId = new Map(rows.map((r) => [String(r.id || r.name || "").toLowerCase(), r]));
  return LAMP_ORDER.map((id) => {
    const hit = byId.get(id) || {};
    return {
      id,
      label: String(hit.label || id).toUpperCase(),
      state: _lampState(hit.state || hit.status),
      line: operatorSay(hit.reason || hit.line || hit.why || hit.detail || "", "—"),
    };
  });
}

/**
 * @param {{ items?: object[], tonight?: object, lamps?: object, why?: string, aiOn?: boolean } | null} aiPack
 * @param {{ isAdmin?: boolean }} [opts]
 */
export function buildTonightWatchHtml(aiPack, opts = {}) {
  const isAdmin = !!opts.isAdmin;
  const pack = aiPack && typeof aiPack === "object" ? aiPack : {};
  const fields = maicFieldsFromAiItems(pack.items);
  const waitingN = Number(fields.waitingN || 0);
  const leadId = String(fields.leadId || "").trim();
  const pulse = String((pack.tonight && pack.tonight.pulse) || pack.pulse || "").toLowerCase();
  const needsYou = waitingN > 0 || pulse === "needs_you" || !!(pack.tonight && pack.tonight.needs_you);
  const acting = !needsYou && (pulse === "acting" || !!(pack.tonight && pack.tonight.acting));
  const title = needsYou ? "Needs You" : acting ? "Acting" : "Continuing";
  const why = operatorSay(
    (pack.tonight && pack.tonight.line)
    || pack.why
    || fields.reasonLine
    || "",
    needsYou
      ? (waitingN === 1
        ? "Needs You — one fact or one person is missing."
        : `${waitingN} Needs You — a fact or a person is missing.`)
      : acting
        ? "The Watch is acting."
        : "Continuing — the Watch is on the loop. You interrupt only when proof is missing.",
  );
  const lamps = lampsOf(pack);
  const lampHtml = lamps.map((lamp) => `
    <li class="croc-tonight__lamp" data-lamp="${escapeHtml(lamp.id)}" data-state="${escapeHtml(lamp.state)}" title="${escapeHtml(lamp.line)}" tabindex="0">
      <i aria-hidden="true"></i>
      <span class="croc-tonight__lamp-k">${escapeHtml(lamp.label || lamp.id)}</span>
      <span class="croc-tonight__lamp-v">${escapeHtml(lamp.line || "—")}</span>
    </li>`).join("");
  const advertised = Array.isArray(pack.decision_actions) ? pack.decision_actions : [];
  const acts = advertised.length && leadId
    ? decisionSurfaceActionsHtml({
      incidentId: leadId,
      context: "home",
      compact: true,
      actions: advertised,
    })
    : `<p class="muted croc-tonight__quiet">${isAdmin
      ? "Continue stays on the loop. Hold · Takeover appear only when the Watch Needs You."
      : "When you are named, Accept · travel · arrive stay on Home. Close needs two-lens proof."}</p>`;

  return `
  <section class="croc-tonight glass-panel glass-panel--panel" data-tonight="1" data-pulse="${needsYou ? "needs_you" : acting ? "acting" : "watching"}" aria-label="Tonight's Watch">
    <header class="croc-tonight__head">
      <div>
        <p class="croc-tonight__eyebrow">Tonight's Watch</p>
        <h2 class="croc-tonight__title">${title}</h2>
        <p class="croc-tonight__lede">${escapeHtml(why)}</p>
        <p class="muted croc-tonight__brand">${escapeHtml(BRAND_TAGLINE)}</p>
      </div>
      <span class="croc-tonight__badge">${needsYou ? "Needs You" : acting ? "Acting" : "On the loop"}</span>
    </header>
    <ol class="croc-tonight__lamps" role="list" aria-label="Judge · Veto · Act · Prove · Stop">
      ${lampHtml}
    </ol>
    <div class="croc-tonight__acts">${acts}</div>
  </section>`;
}
